import React from "react";
import { usePagination } from "../../../utilities/hookutils";
import { Button } from "semantic-ui-react";
import { NoDataAvailableDiv } from "../../../components/NoDataAvailableDiv";
import OnboardingStatisticsDiv from "./OnboardingStatisticsDiv";
import moment from "moment";
import { formatWrittenDateTimeUtc } from "../../../utilities";
import { DeleteDialog } from "../../../components";
import { routes } from "utilities/routing/generatedRoutes";
import BaseReactSelectDropdown from "components/dropdowns/reactselect/BaseReactSelectDropdown";
import { ContactSidePanel } from "components/SidePanel/contact/ContactSidePanel";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import { findEnumLabel } from "utilities/enumutils";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import { Loading } from "components/panels";

interface Option {
  label: string
  value: server.dto.ClientOnboardingDashboardFilterOptions
};

const options: Option[] = [
  {
    label: "Needs attention",
    value: "needsAttention"
  },
  {
    label: "Only initialized",
    value: "onlyInitialized"
  },
  {
    label: "Waiting to fill in form",
    value: "waitingForFillForm"
  },
  {
    label: "Ready to create client account",
    value: "readyToCreateClientAccount"
  },
  {
    label: "Client account was created",
    value: "clientAccountWasCreated"
  }
];

const OnboardingAdminOneDashboardPanel = () => {
  const maxPerPage = 8;

  const [data, setData] = React.useState<server.dto.ClientOnboardingDashboard>(undefined);
  const [isLoading, setIsLoading] = React.useState(true);
  const [filter, setFilter] = React.useState<Option>(options[0]);
  const [deletingOnboardingId, setDeletingOnboardingId] = React.useState(undefined);

  const paginate = async (paginationData: server.dto.PaginationData) => {
    const result = await window.CoreApi.ClientOnboarding.Dashboard({
      filter: filter.value,
      page: paginationData.page,
      perPage: paginationData.perPage
    });
    setIsLoading(false);
    setData(result.data.data);

    return result.data.pagination;
  };

  const pagination = usePagination(maxPerPage, paginate, {
    total: 1,
    page: 1,
    pageCount: 1,
    perPage: maxPerPage,
    maxPerPage,
    hasResults: true
  });

  const onDeleteOnboardingClicked = (onboardingId: number) => {
    setDeletingOnboardingId(onboardingId);
  };

  const onStartDeleting = async () => {
    try {
      const res = await window.CoreApi.ClientOnboarding.Remove(deletingOnboardingId);
      setDeletingOnboardingId(undefined);

      if (res.data.errors.length > 0) {
        alert("There were errors with deleting:\n " + res.data.errors.map(e => e.message).join("\n"));
      }

      await pagination.onPaginate(pagination.page);
    } catch (e) {
      alert("Something unexpected happened.\n" + e);
      console.error(e);
    }
  };

  // Reloads page when on initial load or when filter changes
  React.useEffect(() => {
    pagination.onPaginate(1);
  }, [filter]);

  if (isLoading) {
    return <SectionPanel.Panel
      header={<PanelSegment.Title title="Onboarding" />}
    >
      <Loading />
    </SectionPanel.Panel>
  }

  if (data == null) {
    return <SectionPanel.Panel
      header={<PanelSegment.Title title="Onboarding" />}
    >
      <NoDataAvailableDiv />
    </SectionPanel.Panel>;
  }

  return <SectionPanel.Panel
    header={<>
      <PanelSegment.Title title="Onboarding" />
      {data?.list.length > 0 && <div>
        {pagination.Component}
      </div>}
    </>}
  >

    <div className="padding-bigger">
      <div className="flex margin-bottom">
        <div className="grow1">
          <div style={{ width: 300 }}>
            <BaseReactSelectDropdown
              options={options}
              value={filter}
              onChange={(value) => setFilter(value)}
              getLabel={(option) => option.label}
              getValue={(option) => option.value}
              excludeKeys={[]}
              config={{}}
            />
          </div>
        </div>


      </div>
      {data?.list.length === 0 && <div className="margin-top-large"><NoDataAvailableDiv /></div>}

      {data?.list.length > 0 && <div>

        <div className="list-with-gaps line-layout">
          <div className="list-with-gaps-body">
            {data?.list.map((d) => {
              const displayLink = d.lifeCycle === "initialized" || d.lifeCycle === "setYourDetails" || d.lifeCycle === "configureSubscription" || d.lifeCycle === "paymentSetup";

              const hasProvidedSubscription = d.lifeCycle === "paymentSetup" || d.lifeCycle === "readyForCreateClientAccount" || d.lifeCycle === "clientAccountCreated";

              const lastModifiedMoment = moment.utc(d.lastModifiedUtc);
              const isExpired = moment.utc().diff(lastModifiedMoment, "days") > serverReference.constants.clientOnboarding.expirationInDays;

              return <div key={d.clientOnboardingId}>
                <div style={{ flex: 1 }}>
                  <div><h4>{d.lifeCycle}</h4></div>

                  {d.trial && <div className="s1_tag" style={{ backgroundColor: "#d35400" }}>Trial</div>}
                  {d.userCount === 1 && <div>1 user</div>}
                  {d.userCount > 1 && <div>{d.userCount} users</div>}

                  {hasProvidedSubscription && !d.trial && <div className="s1_tag green-bg">{findEnumLabel(d.billingCycle, serverReference.billingCycles)}</div>}

                  <div>
                    {displayLink && !isExpired && <span><a href={d.landingPageUrl}>Link</a></span>}
                    {displayLink && isExpired && <span className="s1_red">Expired</span>}
                    <span> | id:{d.clientOnboardingId}</span>
                  </div>

                  <div className="s1_gray" title={formatWrittenDateTimeUtc(lastModifiedMoment)}>{lastModifiedMoment.fromNow()}</div>
                </div>

                <div style={{ flex: 1 }}>
                  {(d.client != null) && <><div><a href={routes.client_DetailAdminOne(d.client.clientId).absolute} className="table-main-link">{d.client.name}</a></div><hr /></>}

                  {(d.client == null) && <>
                    {d.contact == null && <div>
                      <div><h4>{d.personName.fullName}</h4></div>
                      <div className="s1_small">{d.email}</div>
                    </div>}

                    {d.contact != null && <div>
                      <div>
                        <ContactSidePanel
                          trigger={<h4><a>{d.contact.name.fullName}</a></h4>}
                          contactId={d.contact.contactId}
                        />
                      </div>
                      <div className="s1_small">{d.email}</div>
                    </div>}

                    <hr />

                    {(d.company == null) && <div>No company provided (yet)</div>}

                    {(d.company != null) && <div>
                      <div>
                        <CompanySidePanel
                          trigger={<h4><a>{d.company.name}</a></h4>}
                          companyId={d.company.companyId}
                        />
                      </div>
                      <div className="text-sublabel">{d.company.cityCountry}</div>
                    </div>}

                  </>}
                </div>

                <div style={{ textAlign: "right" }}>
                  {d.lifeCycle === "readyForCreateClientAccount" && <div>
                    <Button
                      primary
                      type="button"
                      style={{ width: 90 }}
                      onClick={() => window.location.href = `${routes.client_NewAdminOne().absolute}?clientOnboardingId=${d.clientOnboardingId}`}
                    >
                      Create
                    </Button>
                  </div>}

                  {d.lifeCycle !== "clientAccountCreated" && <div
                    className={d.lifeCycle === "readyForCreateClientAccount" ? "margin-top-small" : undefined}
                  >
                    <Button
                      color="red"
                      type="button"
                      style={{ width: 90 }}
                      onClick={() => onDeleteOnboardingClicked(d.clientOnboardingId)}
                    >
                      {RESX.Buttons.btnDelete.toLocaleUpperCase()}
                    </Button>
                  </div>}
                </div>
              </div>;
            })}
          </div>
        </div>
      </div>
      }
    </div >

    {
      data && <OnboardingStatisticsDiv
        value={data}
        onClick={(f) => setFilter(options.find(o => o.value === f))}
      />
    }

    {
      deletingOnboardingId != null && <DeleteDialog
        isOpen={deletingOnboardingId}
        onDelete={onStartDeleting}
        handleClose={() => setDeletingOnboardingId(undefined)}
        message={RESX.GeneralWarnings.DeleteSure}
      />
    }
  </SectionPanel.Panel>;
};

export default OnboardingAdminOneDashboardPanel;